import dayjs from "dayjs";
import { useMemo } from "react";
import { Price } from "../../price";
import {
  currentJstTradingDate,
  nextJstTradingDate,
  useSpotPrices,
} from "./queries";

/**
 * Lookup key for a spot price.
 *
 * Keyed on the **instant**, not the timestamp text. `Contract.delivery_start`
 * comes off NATS as UTC (`...Z`) while `SpotPrice.deliveryStart` carries a
 * +09:00 offset — the same moment spelled two different ways, so a string
 * comparison would match nothing at all and look exactly like "no data".
 */
export const spotKey = (areaCode: string, deliveryStart: string): string =>
  `${areaCode}|${dayjs(deliveryStart).valueOf()}`;

export type SpotPriceLookup = ReadonlyMap<string, Price>;

const EMPTY: SpotPriceLookup = new Map<string, Price>();

/**
 * Spot prices for the trading days the order book can currently show, indexed
 * by {@link spotKey}.
 *
 * Covers today **and** tomorrow in Tokyo. Grid rows are open, unexpired
 * contracts and gate closure is about an hour before delivery, so the visible
 * window runs forward from roughly now+1h and crosses into tomorrow's trading
 * day; late in the Tokyo session every visible row belongs to tomorrow. One
 * request covers both — the provider takes an inclusive range, and a day whose
 * auction has not cleared is simply absent from the response.
 *
 * The returned map is referentially stable between fetches, so callers can put
 * it in a `useMemo` dependency array without adding churn to the 500ms order
 * book tick.
 */
export const useSpotPriceLookup = (): SpotPriceLookup => {
  // Derived through Asia/Tokyo, never browser-local: a desk in Europe is a day
  // behind Tokyo for most of its working afternoon and would ask for the wrong
  // pair of days.
  const from = currentJstTradingDate();
  const to = nextJstTradingDate();

  const { spotPrices } = useSpotPrices(from, to);

  return useMemo(() => {
    if (spotPrices === undefined || spotPrices.length === 0) {
      return EMPTY;
    }

    const lookup = new Map<string, Price>();
    for (const price of spotPrices) {
      if (price.value == null) {
        continue;
      }
      lookup.set(
        spotKey(price.areaCode, price.deliveryStart),
        Price.fromYenPerKwh(price.value)
      );
    }
    return lookup;
  }, [spotPrices]);
};
